"use client"
import CountInput from "@/components/ui/CountInput"
import Link from "next/link"
import Image from "next/image"
import { useState, useRef, useEffect } from "react"
import { useRouter } from "next/navigation"
import { showToast } from "@/components/ui/Toast"
import Icon from "@/components/ui/Icon"
import { useSession } from "@/lib/hooks/useSession"
import { compressImage } from "@/lib/images/compress"
import { MAX_PHOTOS_ABSOLUTE } from "@/lib/payments/plans"

const CITIES = [
  "Harare", "Bulawayo", "Mutare", "Gweru", "Victoria Falls",
  "Masvingo", "Kwekwe", "Chinhoyi", "Bindura", "Marondera",
  "Hwange", "Zvishavane", "Kadoma", "Karoi", "Kariba",
  "Beitbridge", "Chegutu",
]

const CITY_SUBURBS: Record<string, string[]> = {
  "Harare": ["Avondale", "Belvedere", "Bluffhill", "Borrowdale", "Borrowdale Brooke", "Budiriro", "Chisipite", "Eastlea", "Glen Lorne", "Greendale", "Gunhill", "Hatfield", "Highlands", "Kamfinsa", "Kuwadzana", "Mabelreign", "Madokero", "Mbare", "Msasa", "Mt Pleasant", "Newlands", "Northgate", "Pomona", "Tynwald", "Westgate"],
  "Bulawayo": ["Bellevue", "Burnside", "Emganwini", "Famona", "Hillside", "Luveve", "Makokoba", "Matsheumhlope", "Nkulumane", "Pumula", "Queens Park", "Selborne Park", "Suburbs", "Waterford", "Woodville"],
  "Mutare": ["Chikanga", "Dangamvhu", "Greenside", "Hobhouse", "Morningside", "Murambi", "Palmerstone", "Sakubva"],
  "Gweru": ["Ascot", "Mambo", "Mkoba", "Riverside", "Senga", "Windsor Park", "Warrender Heights", "Mtapa", "Woodlands"],
  "Victoria Falls": ["Chinotimba", "Manyika", "Mkhosana"],
  "Masvingo": ["Mucheke", "Rhodene", "Runyararo", "Target Kopje"],
  "Kwekwe": ["Amaveni", "Mbizo", "Newtown", "Rimuka", "Globe and Phoenix", "Sebakwe"],
  "Chinhoyi": ["Chikonohono", "Cold Comfort", "Hunyani Hills", "Alaska", "Lomagundi"],
  "Bindura": ["Chipadze", "Chiwaridzo", "Trojan", "Shamva"],
  "Marondera": ["Windsor Park", "Grasslands", "Dombotombo", "Cherutombo"],
  "Hwange": ["Baobab", "Lukosi", "Ngamo", "Dete"],
  "Zvishavane": ["Mandava", "Lundi", "Shabanie", "Mimosa"],
  "Kadoma": ["Rimuka", "Ngezi", "Eiffel Flats", "Chakari"],
  "Karoi": ["Kasimure", "Tengwe", "Hurungwe"],
  "Kariba": ["Mahombekombe", "Nyamhunga", "Charara", "Andora"],
  "Beitbridge": ["Dulivhadzimu", "Shashi", "Beitbridge West"],
  "Chegutu": ["Chemhanza", "Mhandu", "Hartley"],
}

const REMOVAL_REASONS = [
  "Property has been sold",
  "Property has been rented",
  "I changed my mind about selling/renting",
  "I found a tenant/buyer through another channel",
  "Listing details were incorrect",
  "Other",
]

export default function ListPropertyPage() {
  const router = useRouter()
  const { user } = useSession({ required: true, redirectTo: "/auth/login?next=/list-property" })
  const [step, setStep] = useState(1)

  const [kycStatus, setKycStatus] = useState<string | null>(null)
  // The wizard used to hard-cap at 30 while Agent Pro sells 40 and the server
  // allows up to MAX_PHOTOS_ABSOLUTE. Read the real limit from the plan.
  const [photoLimit, setPhotoLimit] = useState(30)

  // Role guard: developers manage projects/units (not single listings), buyers browse.
  // Only agents and FSBO landlords list individual properties here.
  useEffect(() => {
    if (!user) return
    if (user.role === "DEVELOPER") { router.replace("/dashboard"); return }
    if (user.role === "BUYER") { router.replace("/listings"); return }
    // Surface the KYC requirement upfront — the API hard-requires APPROVED KYC to
    // publish, so warn before the user fills out the whole form and fails at submit.
    fetch("/api/kyc").then(r => r.json()).then(d => setKycStatus(d.data?.status ?? "NONE")).catch(() => {})
    fetch("/api/subscriptions/status")
      .then(r => r.json())
      .then(d => {
        const n = Number(d.data?.photoLimit)
        if (Number.isFinite(n) && n > 0) setPhotoLimit(Math.min(n, MAX_PHOTOS_ABSOLUTE))
      })
      .catch(() => {})
  }, [user, router])
  const [photos, setPhotos] = useState<{ url: string; file: File }[]>([])
  const [uploading, setUploading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [wentToReview, setWentToReview] = useState(false)
  const [form, setForm] = useState({
    title: "", description: "", price: "", priceType: "SALE", propertyType: "HOUSE",
    suburb: "", city: "", address: "", beds: "3", baths: "2", floorArea: "", standSize: "",
    hasTitleDeed: false,
    accuracyPledge: false,
  })
  const [suburbSuggestions, setSuburbSuggestions] = useState<string[]>([])
  const [features, setFeatures] = useState<string[]>([])
  const [titleDeedFile, setTitleDeedFile] = useState<File | null>(null)
  const [shareDetails, setShareDetails] = useState({
    roomsAvailable: 1,
    roomType: "SINGLE" as "SINGLE" | "DOUBLE" | "ENSUITE" | "SHARED",
    furnished: "FURNISHED" as "FURNISHED" | "UNFURNISHED" | "PART_FURNISHED",
    billsIncluded: false,
    availableFrom: "",
    minStayMonths: null as number | null,
    studentsWelcome: true,
    smokingAllowed: false,
    petsAllowed: false,
    couplesWelcome: true,
    genderPref: "ANY" as "ANY" | "FEMALE_ONLY" | "MALE_ONLY",
    houseRules: "",
  })
  const [submitError, setSubmitError] = useState("")
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const fileRef = useRef<HTMLInputElement>(null)
  const titleDeedRef = useRef<HTMLInputElement>(null)
  const totalSteps = 4

  useEffect(() => {
    // P4: Restore draft
    try {
      const draft = localStorage.getItem("ze_listing_draft")
      if (draft) {
        const saved = JSON.parse(draft)
        if (saved.form) {
          setForm(f => ({ ...f, ...saved.form }))
          if (saved.form.city) setSuburbSuggestions(CITY_SUBURBS[saved.form.city] ?? [])
        }
        if (saved.features) setFeatures(saved.features)
        if (saved.step && saved.step >= 1 && saved.step <= 3) setStep(saved.step)
      }
    } catch {}
  }, [])

  // P4: Auto-save draft on form changes
  useEffect(() => {
    try {
      localStorage.setItem("ze_listing_draft", JSON.stringify({ form, features, step }))
    } catch {}
  }, [form, features, step])

  const allFeatures = ["Borehole fitted", "Backup water tank", "Solar power", "Pre-paid ZESA meter", "Swimming pool", "Durawall perimeter", "Electric fence", "Staff quarters", "Built-in cupboards", "Granite countertops", "Fibre internet", "Tarred road access", "Double garage", "Title deed ready", "24/7 security", "Backup generator", "Irrigation system"]

  const pricePerSqm = form.price && form.floorArea ? Math.round(parseFloat(form.price) / parseFloat(form.floorArea)) : 0
  const priceWarning = pricePerSqm > 0 && form.priceType === "SALE" && (pricePerSqm > 4000 || (pricePerSqm < 50 && parseFloat(form.price) > 1000))

  function toggleFeature(f: string) {
    setFeatures(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f])
  }

  async function handlePhotoUpload(files: FileList) {
    const remaining = Math.max(0, photoLimit - photos.length)
    const newPhotos: { url: string; file: File }[] = []
    for (let i = 0; i < Math.min(files.length, remaining); i++) {
      newPhotos.push({ url: URL.createObjectURL(files[i]), file: files[i] })
    }
    // Say so instead of dropping the overflow silently.
    if (files.length > remaining) {
      showToast(
        remaining === 0
          ? `You have reached your plan limit of ${photoLimit} photos.`
          : `Only ${remaining} more photo${remaining === 1 ? "" : "s"} fit on your plan (${photoLimit} max) — ${files.length - remaining} were not added.`,
        "error",
      )
    }
    setPhotos(prev => [...prev, ...newPhotos])
  }

  function validateStep1(): boolean {
    const errs: Record<string, string> = {}
    if (!form.title.trim() || form.title.trim().length < 2) errs.title = "Please enter a listing title (at least 2 characters)"
    if (!form.price || isNaN(parseFloat(form.price)) || parseFloat(form.price) <= 0) errs.price = "Please enter a valid price"
    if (!form.city) errs.city = "Please select a city"
    if (!form.suburb.trim() || form.suburb.trim().length < 2) errs.suburb = "Please enter the suburb"
    if (!form.address.trim() || form.address.trim().length < 5) errs.address = "Please enter the full address (at least 5 characters)"
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleContinue() {
    if (step === 1 && !validateStep1()) return
    setStep(s => Math.min(totalSteps, s + 1))
  }

  async function handleSubmit() {
    setUploading(true)
    setSubmitError("")
    try {
      if (!user?.id) { showToast("Please sign in to list a property", "error"); setUploading(false); return }
      const res = await fetch("/api/listings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price),
          beds: Number.parseInt(form.beds, 10) || 0,
          baths: Number.parseInt(form.baths, 10) || 0,
          floorArea: form.floorArea ? parseFloat(form.floorArea) : undefined,
          standSize: form.standSize ? parseFloat(form.standSize) : undefined,
          features,
          ...(form.propertyType === "ROOM" ? {
            shareDetails: {
              ...shareDetails,
              roomsAvailable: Math.max(1, shareDetails.roomsAvailable || 1),
              availableFrom: shareDetails.availableFrom ? new Date(shareDetails.availableFrom).toISOString() : undefined,
              minStayMonths: shareDetails.minStayMonths ?? undefined,
            },
          } : {}),
        }),
      })
      const data = await res.json()
      if (!res.ok && !data.success) {
        const errMsg = typeof data.error === "string" ? data.error : "Submission failed. Please check all required fields and try again."
        // P15: Surface price/m² rejection specifically
        if (errMsg.toLowerCase().includes("price") || errMsg.toLowerCase().includes("m²") || errMsg.toLowerCase().includes("per sqm")) {
          const price = parseFloat(form.price) || 0
          const area = parseFloat(form.floorArea) || 0
          const ppm = area > 0 ? Math.round(price / area) : 0
          setSubmitError(`Your price-per-m² ($${ppm}/m²) is outside the accepted range ($10-$5,000/m²). For a ${area}m² property, try a price between $${(area * 10).toLocaleString()} and $${(area * 5000).toLocaleString()}.`)
        } else if (res.status === 409) {
          // A previous attempt already created this listing (typically a dropped
          // connection mid-upload). Point the seller at it instead of dead-ending
          // them on "duplicate address" with no way forward.
          setSubmitError(
            "You already have a listing at this address — it was probably created by an earlier attempt that did not finish. " +
            "Open it from your dashboard to add the remaining photos and submit it for review.",
          )
          try { localStorage.removeItem("ze_listing_draft") } catch {}
        } else {
          setSubmitError(errMsg)
        }
        setUploading(false)
        return
      }
      if (data.success) {
        // Remember the created listing IMMEDIATELY. If the connection drops
        // during the photo loop below, the row already exists — without this the
        // user retried, POST /api/listings hit the duplicate-address guard, and
        // they were permanently locked out of their own half-created listing.
        try { localStorage.setItem("ze_listing_pending_id", data.data.id) } catch {}

        let photosFailed = 0
        for (const photo of photos) {
          // A THROWN fetch (dropped connection, timeout) used to abort the whole
          // submit, skipping the review PATCH and the draft cleanup. Treat it
          // exactly like a non-ok response: count it and keep going.
          try {
            const fd = new FormData()
            // Compress before upload — raw phone photos exceed the server's body
            // limit and are slow to send on mobile data
            fd.append("file", await compressImage(photo.file))
            fd.append("listingId", data.data.id)
            const upRes = await fetch("/api/upload", { method: "POST", body: fd })
            if (!upRes.ok) {
              photosFailed++
              const upErr = await upRes.json().catch(() => ({}))
              console.error("Photo upload failed:", upErr.error ?? upRes.status)
            } else {
              const upData = await upRes.json().catch(() => ({}))
              if (upData.warning) showToast(upData.warning, "warning")
            }
          } catch (photoErr) {
            photosFailed++
            console.error("Photo upload threw:", photoErr)
          }
        }
        if (titleDeedFile) {
          const fd = new FormData()
          fd.append("file", titleDeedFile)
          fd.append("listingId", data.data.id)
          try {
            const docRes = await fetch("/api/upload/document", { method: "POST", body: fd })
            if (!docRes.ok) {
              const docErr = await docRes.json().catch(() => ({}))
              showToast(`Title deed could not be saved: ${docErr.error ?? "storage error"}. Re-upload from your dashboard.`, "warning")
            }
          } catch {
            showToast("Title deed could not be saved (connection lost). Re-upload from your dashboard.", "warning")
          }
        }
        if (photosFailed > 0) setSubmitError(`${photosFailed} of ${photos.length} photo(s) failed to upload (storage error). Your listing was saved, re-upload the missing photos from your dashboard.`)
        // Verified owners: auto-submit for review so it can go live. Unverified: it stays a draft.
        if (kycStatus === "APPROVED") {
          await fetch("/api/listings/" + data.data.id, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reviewRequested: true }) }).catch(() => {})
          setWentToReview(true)
        }
        localStorage.removeItem("ze_listing_draft")
        try { localStorage.removeItem("ze_listing_pending_id") } catch {}
        setSubmitted(true)
      } else {
        setSubmitError(typeof data.error === "string" ? data.error : "Submission failed. Please try again.")
      }
    } catch (err) {
      setSubmitError("Something went wrong. Please check your connection and try again.")
    }
    setUploading(false)
  }

  if (submitted) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 480 }}>
        <div style={{ fontSize: 64, marginBottom: 24 }}>✓</div>
        <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, fontWeight: 700, color: "var(--navy)", marginBottom: 12 }}>{wentToReview ? "Listing submitted" : "Listing saved as a draft"}</h1>
        <p style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.6, marginBottom: 32 }}>
          {wentToReview
            ? "Your listing has been submitted for review. Our trust team will verify it within 24 hours. You'll get an SMS and email confirmation when it goes live."
            : "Your listing is safely saved. To publish it, verify your identity (KYC) — then it'll be submitted for review automatically. Complete verification from your dashboard."}
        </p>
        {submitError && (
          <div style={{ marginBottom: 24, padding: "12px 16px", background: "rgba(200,150,62,0.08)", border: "1px solid rgba(200,150,62,0.3)", borderRadius: 10, fontSize: 13, color: "var(--gold-deep)", textAlign: "left", lineHeight: 1.6 }}>{submitError}</div>
        )}
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Link href="/dashboard" className="btn-navy">Go to dashboard</Link>
          <Link href="/listings" className="btn-outline">Browse listings</Link>
        </div>
      </div>
    </div>
  )

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <div style={{ background: "var(--navy)", padding: "48px 24px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 40, fontWeight: 700, color: "var(--cream)", marginBottom: 8 }}>List your <em style={{ color: "var(--gold)" }}>property</em>.</h1>
          <p style={{ color: "rgba(253,251,247,0.75)", fontSize: 15 }}>Step {step} of {totalSteps}</p>
          <div style={{ display: "flex", gap: 4, marginTop: 20 }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < step ? "var(--gold)" : "rgba(253,251,247,0.2)" }}></div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "48px 24px" }}>
        {kycStatus && kycStatus !== "APPROVED" && (
          <div style={{ marginBottom: 28, padding: "16px 20px", background: kycStatus === "PENDING" ? "rgba(200,150,62,0.08)" : "rgba(196,68,68,0.06)", border: `1px solid ${kycStatus === "PENDING" ? "rgba(200,150,62,0.3)" : "rgba(196,68,68,0.25)"}`, borderRadius: 12, display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>{kycStatus === "PENDING" ? "⏳" : "🪪"}</span>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, color: kycStatus === "PENDING" ? "var(--gold-deep)" : "var(--red)", fontSize: 14, marginBottom: 2 }}>
                {kycStatus === "PENDING" ? "Your KYC is under review" : "Identity verification required"}
              </div>
              <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.5 }}>
                {kycStatus === "PENDING"
                  ? "You can prepare your listing now, but it can only go live once your KYC is approved (usually within 24 hours)."
                  : "Listings can only be published after your identity is verified. You can fill this in now, but you'll need approved KYC to submit."}
              </div>
            </div>
            {kycStatus !== "PENDING" && (
              <Link href="/kyc?next=/list-property" style={{ padding: "10px 18px", background: "var(--navy)", color: "var(--cream)", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" }}>Verify identity</Link>
            )}
          </div>
        )}
        {step === 1 && (
          <div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>Property details</h2>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 32 }}>Tell us about the property you are listing.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Listing title <span style={{ color: "var(--red-text)" }}>*</span></label>
                <input value={form.title} onChange={e => { setForm(f => ({ ...f, title: e.target.value })); setFieldErrors(fe => ({ ...fe, title: "" })) }} placeholder="e.g. Modern 4-Bed Family Home with Pool" style={{ width: "100%", padding: "12px 14px", border: `1px solid ${fieldErrors.title ? "var(--red)" : "var(--line)"}`, borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
                {fieldErrors.title && <div style={{ marginTop: 5, fontSize: 12, color: "var(--red-text)" }}>{fieldErrors.title}</div>}
              </div>
              <div className="mob-grid-2" style={{ gap: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Listing type</label>
                  {form.propertyType === "ROOM" ? (
                    <div style={{ padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, background: "var(--cream-warm)", color: "var(--green-text)", fontWeight: 600 }}>
                      🔒 Rent only — room sharing
                    </div>
                  ) : (
                    <select value={form.priceType} onChange={e => setForm(f => ({ ...f, priceType: e.target.value }))} style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }}>
                      <option value="SALE">For Sale</option>
                      <option value="RENT">For Rent</option>
                    </select>
                  )}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Property type</label>
                  <select value={form.propertyType} onChange={e => {
                    const pt = e.target.value
                    setForm(f => ({ ...f, propertyType: pt, ...(pt === "ROOM" ? { priceType: "RENT" } : {}) }))
                  }} style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }}>
                    <option value="HOUSE">House</option>
                    <option value="APARTMENT">Apartment</option>
                    <option value="CLUSTER">Cluster</option>
                    <option value="LAND">Land/Stand</option>
                    <option value="COMMERCIAL">Commercial</option>
                    <option value="ROOM">🛏️ Room to Let</option>
                  </select>
                </div>
              </div>
              <div className="mob-grid-2" style={{ gap: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Price (USD) <span style={{ color: "var(--red-text)" }}>*</span></label>
                  <input value={form.price} onChange={e => { setForm(f => ({ ...f, price: e.target.value })); setFieldErrors(fe => ({ ...fe, price: "" })) }} placeholder="285000" type="number" style={{ width: "100%", padding: "12px 14px", border: `1px solid ${fieldErrors.price ? "var(--red)" : "var(--line)"}`, borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
                  {fieldErrors.price && <div style={{ marginTop: 5, fontSize: 12, color: "var(--red-text)" }}>{fieldErrors.price}</div>}
                  {!fieldErrors.price && priceWarning && <div style={{ marginTop: 6, fontSize: 12, color: "var(--gold-deep)", background: "rgba(200,150,62,0.08)", border: "1px solid rgba(200,150,62,0.2)", borderRadius: 6, padding: "6px 10px" }}>{"USD " + pricePerSqm + "/m² is outside the typical " + (form.city || "Zimbabwe") + " range. Double-check your price and floor area."}</div>}
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>City <span style={{ color: "var(--red-text)" }}>*</span></label>
                  <select
                    value={form.city}
                    onChange={e => {
                      const city = e.target.value
                      setForm(f => ({ ...f, city, suburb: "" }))
                      setSuburbSuggestions(CITY_SUBURBS[city] ?? [])
                      setFieldErrors(fe => ({ ...fe, city: "" }))
                    }}
                    style={{ width: "100%", padding: "12px 14px", border: `1px solid ${fieldErrors.city ? "var(--red)" : "var(--line)"}`, borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }}
                  >
                    <option value="">Select city…</option>
                    {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {fieldErrors.city && <div style={{ marginTop: 5, fontSize: 12, color: "var(--red-text)" }}>{fieldErrors.city}</div>}
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Suburb <span style={{ color: "var(--red-text)" }}>*</span></label>
                <input
                  list="suburb-suggestions"
                  value={form.suburb}
                  onChange={e => { setForm(f => ({ ...f, suburb: e.target.value })); setFieldErrors(fe => ({ ...fe, suburb: "" })) }}
                  placeholder={form.city ? `e.g. ${suburbSuggestions[0] ?? "Suburb name"}` : "Select a city first"}
                  disabled={!form.city}
                  style={{ width: "100%", padding: "12px 14px", border: `1px solid ${fieldErrors.suburb ? "var(--red)" : "var(--line)"}`, borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: form.city ? "var(--cream)" : "var(--cream-warm)", outline: "none", opacity: form.city ? 1 : 0.6 }}
                />
                {suburbSuggestions.length > 0 && (
                  <datalist id="suburb-suggestions">
                    {suburbSuggestions.map(s => <option key={s} value={s} />)}
                  </datalist>
                )}
                {fieldErrors.suburb && <div style={{ marginTop: 5, fontSize: 12, color: "var(--red-text)" }}>{fieldErrors.suburb}</div>}
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Full address <span style={{ color: "var(--red-text)" }}>*</span></label>
                <input value={form.address} onChange={e => { setForm(f => ({ ...f, address: e.target.value })); setFieldErrors(fe => ({ ...fe, address: "" })) }} placeholder={form.city ? `e.g. 14 Main Road, ${form.suburb || form.city}` : "e.g. 14 Main Road, Suburb, City"} style={{ width: "100%", padding: "12px 14px", border: `1px solid ${fieldErrors.address ? "var(--red)" : "var(--line)"}`, borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
                {fieldErrors.address && <div style={{ marginTop: 5, fontSize: 12, color: "var(--red-text)" }}>{fieldErrors.address}</div>}
              </div>
              <div className="mob-grid-3" style={{ gap: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Bedrooms</label>
                  {/* Was a dropdown that stopped at 6. Now 0 to 10, then any number. */}
                  <CountInput label="Bedrooms" zeroLabel="N/A" value={form.beds} onChange={v => setForm(f => ({ ...f, beds: v }))} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Bathrooms</label>
                  {/* Was a dropdown that stopped at 5. Now 0 to 10, then any number. */}
                  <CountInput label="Bathrooms" zeroLabel="N/A" value={form.baths} onChange={v => setForm(f => ({ ...f, baths: v }))} />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Floor area m²</label>
                  <input value={form.floorArea} onChange={e => setForm(f => ({ ...f, floorArea: e.target.value }))} placeholder="480" type="number" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
                </div>
              </div>
              {/* ── Share Details panel (Room to Let only) ── */}
              {form.propertyType === "ROOM" && (
                <div style={{ background: "var(--cream-warm)", border: "1.5px solid var(--gold)", borderRadius: 14, padding: "22px 20px" }}>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700, color: "var(--navy)", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                    🛏️ Room Share Details
                    <span style={{ background: "var(--gold)", color: "var(--navy)", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 3 }}>NEW</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--gold-deep)", background: "rgba(200,150,62,0.1)", border: "1px solid rgba(200,150,62,0.25)", borderRadius: 8, padding: "10px 13px", marginBottom: 20 }}>
                    💡 Be specific with your house rules — it saves time and helps the right flatmate find you.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Rooms available</label>
                      {/* Was buttons 1 to "5+", where "5+" quietly saved 5. Now 1 to 10, then any number. */}
                      <CountInput label="Rooms available" min={1} value={shareDetails.roomsAvailable}
                        onChange={v => setShareDetails(s => ({ ...s, roomsAvailable: Number(v) || 0 }))} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Room type</label>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {(["SINGLE","DOUBLE","ENSUITE","SHARED"] as const).map(t => (
                          <button key={t} type="button" onClick={() => setShareDetails(s => ({ ...s, roomType: t }))}
                            style={{ padding: "8px 12px", borderRadius: 20, border: `1.5px solid ${shareDetails.roomType === t ? "var(--navy)" : "var(--line)"}`, background: shareDetails.roomType === t ? "var(--navy)" : "var(--cream)", color: shareDetails.roomType === t ? "var(--cream)" : "var(--navy)", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                            {t === "ENSUITE" ? "En-suite" : t.charAt(0) + t.slice(1).toLowerCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Furnished</label>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {([["FURNISHED","Furnished"],["PART_FURNISHED","Part furnished"],["UNFURNISHED","Unfurnished"]] as const).map(([v,l]) => (
                          <button key={v} type="button" onClick={() => setShareDetails(s => ({ ...s, furnished: v }))}
                            style={{ padding: "8px 12px", borderRadius: 20, border: `1.5px solid ${shareDetails.furnished === v ? "var(--navy)" : "var(--line)"}`, background: shareDetails.furnished === v ? "var(--navy)" : "var(--cream)", color: shareDetails.furnished === v ? "var(--cream)" : "var(--navy)", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Bills included?</label>
                      <div style={{ display: "flex", gap: 6 }}>
                        {([["Yes",true],["No",false]] as const).map(([l,v]) => (
                          <button key={l} type="button" onClick={() => setShareDetails(s => ({ ...s, billsIncluded: v }))}
                            style={{ padding: "8px 18px", borderRadius: 20, border: `1.5px solid ${shareDetails.billsIncluded === v ? "var(--gold)" : "var(--line)"}`, background: shareDetails.billsIncluded === v ? "rgba(200,150,62,0.12)" : "var(--cream)", color: "var(--navy)", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Available from</label>
                      <input type="date" value={shareDetails.availableFrom} onChange={e => setShareDetails(s => ({ ...s, availableFrom: e.target.value }))}
                        style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Minimum stay</label>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {([[null,"Flexible"],[1,"1 month"],[3,"3 months"],[6,"6 months"],[12,"1 year+"]] as const).map(([v,l]) => (
                          <button key={l} type="button" onClick={() => setShareDetails(s => ({ ...s, minStayMonths: v }))}
                            style={{ padding: "7px 11px", borderRadius: 20, border: `1.5px solid ${shareDetails.minStayMonths === v ? "var(--navy)" : "var(--line)"}`, background: shareDetails.minStayMonths === v ? "var(--navy)" : "var(--cream)", color: shareDetails.minStayMonths === v ? "var(--cream)" : "var(--navy)", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* House rules */}
                  <div style={{ marginTop: 20 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>House rules — click to toggle</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {([
                        ["🎓","Students welcome","studentsWelcome"],
                        ["👫","Couples welcome","couplesWelcome"],
                        ["🚬","Smoking allowed","smokingAllowed"],
                        ["🐾","Pets allowed","petsAllowed"],
                      ] as const).map(([icon,label,key]) => {
                        const on = shareDetails[key]
                        return (
                          <button key={key} type="button" onClick={() => setShareDetails(s => ({ ...s, [key]: !s[key] }))}
                            style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 13px", background: "var(--cream)", border: `1.5px solid ${on ? "var(--green)" : "var(--line)"}`, borderRadius: 9, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                            <span style={{ fontSize: 16 }}>{icon}</span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)", flex: 1 }}>{label}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: on ? "var(--green)" : "var(--muted)" }}>{on ? "✓ Yes" : "✗ No"}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  {/* Gender preference */}
                  <div style={{ marginTop: 16 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Preferred flatmate</label>
                    <div style={{ display: "flex", gap: 6 }}>
                      {([["ANY","Any gender"],["FEMALE_ONLY","Female only"],["MALE_ONLY","Male only"]] as const).map(([v,l]) => (
                        <button key={v} type="button" onClick={() => setShareDetails(s => ({ ...s, genderPref: v }))}
                          style={{ padding: "8px 14px", borderRadius: 20, border: `1.5px solid ${shareDetails.genderPref === v ? "var(--navy)" : "var(--line)"}`, background: shareDetails.genderPref === v ? "var(--navy)" : "var(--cream)", color: shareDetails.genderPref === v ? "var(--cream)" : "var(--navy)", fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Additional rules */}
                  <div style={{ marginTop: 16 }}>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Additional rules <span style={{ fontWeight: 400, textTransform: "none", color: "var(--muted)" }}>— optional</span></label>
                    <textarea value={shareDetails.houseRules} onChange={e => setShareDetails(s => ({ ...s, houseRules: e.target.value.slice(0, 500) }))}
                      rows={3} placeholder="e.g. Quiet household. Cleaning rota in place. No overnight guests on weekdays."
                      style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none", resize: "vertical" }} />
                    <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{shareDetails.houseRules.length}/500</div>
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={5} placeholder="Describe the property in detail..." style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none", resize: "vertical" }} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>Features and amenities</h2>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 32 }}>Select all features that apply.</p>
            <div className="mob-grid-2" style={{ gap: 10 }}>
              {allFeatures.map(f => (
                <label key={f} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", border: "2px solid " + (features.includes(f) ? "var(--navy)" : "var(--line)"), borderRadius: 8, cursor: "pointer", fontSize: 14, background: features.includes(f) ? "rgba(27,45,84,0.04)" : "var(--cream)", userSelect: "none" }}>
                  {/* Real checkbox (visually hidden, not display:none) so the whole
                      amenities step is reachable and toggleable by keyboard. It was
                      a label with onClick and no control inside — mouse only. */}
                  <input
                    type="checkbox"
                    checked={features.includes(f)}
                    onChange={() => toggleFeature(f)}
                    style={{ position: "absolute", opacity: 0, width: 18, height: 18, margin: 0, cursor: "pointer" }}
                  />
                  <div aria-hidden style={{ width: 18, height: 18, borderRadius: 4, border: "2px solid " + (features.includes(f) ? "var(--navy)" : "var(--line)"), background: features.includes(f) ? "var(--navy)" : "transparent", display: "grid", placeItems: "center", flexShrink: 0 }}>
                    {features.includes(f) && <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>✓</span>}
                  </div>
                  {f}
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>Photos and title deed</h2>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 32 }}>Upload up to {photoLimit} photos on your plan. Photos are reviewed by our trust team before your listing goes live.</p>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} onChange={e => e.target.files && handlePhotoUpload(e.target.files)} />
            <input ref={titleDeedRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={e => e.target.files && setTitleDeedFile(e.target.files[0])} />

            {/* A div with onClick cannot be focused or activated by keyboard, so a
                keyboard-only seller could not add a single photo. */}
            <button type="button" onClick={() => fileRef.current?.click()} style={{ display: "block", width: "100%", font: "inherit", border: "2px dashed var(--line)", borderRadius: 14, padding: 40, textAlign: "center", marginBottom: 16, cursor: "pointer", background: "var(--cream-warm)" }}>
              <div style={{ marginBottom: 10 }}><Icon name="Photo" size={32} /></div>
              <div style={{ fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Click to upload photos</div>
              <div style={{ fontSize: 13, color: "var(--muted)" }}>JPEG, PNG or WebP. Max 10MB each. Up to {photoLimit} photos.</div>
            </button>

            {photos.length > 0 && (
              <div className="mob-grid-3" style={{ gap: 10, marginBottom: 20 }}>
                {photos.map((photo, i) => (
                  <div key={i} style={{ position: "relative", borderRadius: 10, overflow: "hidden" }}>
                    <img src={photo.url} alt="" style={{ width: "100%", height: "auto", display: "block" }} loading="lazy" decoding="async" />
                    <button onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))} style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.6)", border: "none", color: "white", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", display: "grid", placeItems: "center" }}><Icon name="X" size={11} /></button>
                    {i === 0 && <div style={{ position: "absolute", bottom: 6, left: 6, background: "var(--gold)", color: "var(--navy)", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4 }}>PRIMARY</div>}
                  </div>
                ))}
              </div>
            )}

            <div style={{ border: "1px solid var(--line)", borderRadius: 14, padding: 24, background: "var(--cream-warm)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 15, marginBottom: 4 }}>Title deed (optional)</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>PDF, JPEG or PNG.</div>
                </div>
                {titleDeedFile && <span style={{ fontSize: 12, color: "var(--green-text)", fontWeight: 600 }}>✓ Uploaded</span>}
              </div>

              <div style={{ background: "rgba(200,150,62,0.08)", border: "1px solid rgba(200,150,62,0.25)", borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontWeight: 700, color: "var(--gold-deep)", fontSize: 12, marginBottom: 6 }}>IMPORTANT, Please read before uploading</div>
                <div style={{ fontSize: 12, color: "var(--ink)", lineHeight: 1.6 }}>
                  Your title deed is stored securely and is <strong>never shown to buyers, agents, or any third party</strong>. Our team checks that it matches this property. Buyers then see "Title deed checked" on your listing, and it adds 20 points to its Trust Score. This is not a Deeds Office search: always use a qualified conveyancer for the actual transfer.
                </div>
              </div>

              <button onClick={() => titleDeedRef.current?.click()} style={{ padding: "10px 20px", background: "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                {titleDeedFile ? "Replace title deed" : "Upload title deed"}
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 28, fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>Review and publish</h2>
            <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 32 }}>Check your listing details before submitting.</p>
            <div style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, padding: 24, marginBottom: 24 }}>
              <div className="mob-grid-2" style={{ gap: 16 }}>
                {[
                  { label: "Title", value: form.title || "Not set" },
                  { label: "Type", value: form.priceType + " · " + form.propertyType },
                  { label: "Price", value: form.price ? "$" + parseFloat(form.price).toLocaleString() : "Not set" },
                  { label: "City", value: form.city || "Not set" },
                  { label: "Suburb", value: form.suburb || "Not set" },
                  { label: "Bedrooms", value: form.beds },
                  { label: "Bathrooms", value: form.baths },
                  { label: "Photos", value: photos.length + " uploaded" },
                  { label: "Title deed", value: titleDeedFile ? "Uploaded (private)" : "Not provided" },
                ].map(item => (
                  <div key={item.label}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 500 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ background: "rgba(61,139,95,0.05)", border: "1px solid rgba(61,139,95,0.2)", borderRadius: 14, padding: 24, marginBottom: 24 }}>
              <div style={{ fontWeight: 700, color: "var(--green-text)", marginBottom: 12 }}>What happens next</div>
              {["Photos are reviewed by our trust team", "Trust Score is computed from your verification", "Listing goes live within 24 hours", "You receive an SMS and email confirmation when live", "Buyers contact you directly via the platform"].map(item => (
                <div key={item} style={{ display: "flex", gap: 8, fontSize: 13, color: "var(--ink)", marginBottom: 8 }}><span style={{ color: "var(--green-text)" }}>✓</span>{item}</div>
              ))}
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 16, border: "2px solid " + (form.accuracyPledge ? "var(--green)" : "var(--line)"), borderRadius: 10, cursor: "pointer", marginBottom: 16, background: form.accuracyPledge ? "rgba(61,139,95,0.04)" : "var(--cream-warm)" }}>
              <input type="checkbox" checked={form.accuracyPledge} onChange={e => setForm(f => ({ ...f, accuracyPledge: e.target.checked }))} style={{ width: 18, height: 18, accentColor: "var(--navy)", marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.6 }}>I confirm all information in this listing is accurate to the best of my knowledge. Submitting false information violates Zim Estates terms and may result in account suspension.</span>
            </label>
            {submitError && (
              <div style={{ padding: "12px 16px", background: "rgba(196,68,68,0.07)", border: "1px solid rgba(196,68,68,0.25)", borderRadius: 10, fontSize: 13, color: "var(--red-text)", marginBottom: 16, lineHeight: 1.5 }}>{submitError}</div>
            )}
            <button onClick={handleSubmit} disabled={uploading || !form.accuracyPledge} style={{ width: "100%", padding: 16, background: uploading || !form.accuracyPledge ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: uploading || !form.accuracyPledge ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {uploading ? "Submitting..." : "Submit listing for review"}
            </button>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 32 }}>
          <button onClick={() => setStep(s => Math.max(1, s - 1))} style={{ padding: "12px 24px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "var(--cream)", color: "var(--navy)", fontFamily: "inherit", opacity: step === 1 ? 0.4 : 1 }} disabled={step === 1}>Back</button>
          {step < totalSteps && <button onClick={handleContinue} style={{ padding: "12px 24px", background: "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Continue</button>}
        </div>
      </div>
    </div>
  )
}